import React, { useEffect, useState, useContext } from 'react';
import {
  Button,
  Card,
  Col,
  Dropdown,
  Form,
  OverlayTrigger,
  Row,
  Tooltip,
  Spinner
} from 'react-bootstrap';
import 'tinymce/skins/ui/oxide/skin.css';
import Flex from 'components/common/Flex';
import PropTypes from 'prop-types';
import FileUpload from 'components/app/support-desk/FileUpload';
import QuillEditor from 'components/QuilEditor';
import { Link } from 'react-router-dom';
import { Controller } from 'react-hook-form';
import MultiSelect from 'components/common/MultiSelect';
import { getUserForPrivateMessages } from 'functions/user';
import ComposeAttachment from './ComposeAttachment';
import AppContext from 'context/Context';

const Reply = ({ setShowReplyForm, title, bodyClassName, onSubmitReply, clearReplyFields }) => {
  const REACT_APP_API_PATH = process.env.REACT_APP_API_PATH;
  const [description, setDescription] = useState(undefined);
  const [attachments, setAttachments] = useState([]);
  const [isPrivateMessage, setIsPrivateMessage] = useState(false);
  const [userForPrivateMessages, setUserForPrivateMessages] = useState(null);
  const [usersForPrivateMessge, setUsersForPrivateMessge] = useState(null);
  
  // Add state for button loading/disabled
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    setDescription(null);
    setAttachments([]);
    setIsSending(false); // Re-enable button when clearing fields
  }, [clearReplyFields]);

  const setAttachmentHandler = (attachments) => {
    console.log('setAttachmentHandler', attachments);
    setAttachments(attachments);
  };

  useEffect(() => {
    loadUserForPrivateMessages();
  }, []);

  const loadUserForPrivateMessages = async () => {
    const token = localStorage.getItem('token');
    const data = await getUserForPrivateMessages(null, token);
    setUserForPrivateMessages(data);
  };

  const onGetContentHandler = async (value) => {
    console.log('onGetContentHandler', value);
    setDescription(value);
  };

  const handleMultiSelectChange = (selectedOptions) => {
    let arr = [];
    for (let i = 0; i < selectedOptions.length; i++) {
      console.log('handleMultiSelectChange', selectedOptions[i].value);
      arr.push({ userId: selectedOptions[i].value });
    }
    setUsersForPrivateMessge(arr);
  };

  const handleSendClick = async () => {
    // Disable button immediately
    setIsSending(true);

    const formData = {
      description,
      attachments,
      usersForPrivateMessge,
      isPrivateMessage
    };

    console.log('handleSendClick formData', formData);

    try {
      // Call the parent's submit handler
      await onSubmitReply(formData);
      
      // Re-enable button after successful send
      setIsSending(false);
    } catch (error) {
      console.error('Error sending message:', error);
      // Re-enable button even if there's an error
      setIsSending(false);
    }
  };

  return (
    <>
      <Card
        as={Form}
        className={bodyClassName}
        style={{ backgroundColor: isPrivateMessage ? '#c2c2c2' : '', marginBottom: '10px' }}
      >
        <Card.Header className="bg-light">
          <h5 className="mb-0">{title ? title : 'New message'}</h5>
        </Card.Header>
        <Card.Body className="p-0">
          {(() => {
            const { config } = useContext(AppContext);
            const { isDark } = config;

            const editorBgColor = isDark ? undefined : '#ffffff';
            const textColor = isDark ? '#f8f9fa' : '#212529';

            return (
              <div
                style={{
                  backgroundColor: editorBgColor,
                  color: textColor,
                  padding: '1rem',
                  borderRadius: '0.375rem'
                }}
              >
                <QuillEditor
                  value={description}
                  onQuilEditorChange={onGetContentHandler}
                  style={{
                    backgroundColor: editorBgColor,
                    color: textColor,
                    border: 'none',
                    minHeight: '150px'
                  }}
                />

                {attachments.length > 0 && (
                  <div
                    className="px-x1 py-3"
                    style={{
                      backgroundColor: isDark ? '#2a2d2e' : '#f8f9fa',
                      borderRadius: '0.375rem',
                      marginTop: '1rem'
                    }}
                  >
                    <Flex direction="column" inline>
                      {attachments.map(attachment => (
                        <ComposeAttachment
                          {...attachment}
                          key={attachment.id}
                        />
                      ))}
                    </Flex>
                  </div>
                )}
              </div>
            );
          })()}
        </Card.Body>

        <Card.Footer>
  <Row className="align-items-center g-2">
    
    {/* Send Button */}
    <Col xs="auto">
      <Button
        type="button"
        variant="primary"
        size="sm"
        className="px-4"
        onClick={handleSendClick}
        disabled={isSending}
      >
        {isSending ? (
          <>
            <Spinner
              as="span"
              animation="border"
              size="sm"
              className="me-1"
            />
            Sending...
          </>
        ) : (
          'Send'
        )}
      </Button>
    </Col>

    {/* MultiSelect (STATIC WIDTH & HEIGHT) */}
    <Col>
      <div style={{ width: '500px' }}>
        <MultiSelect
          options={userForPrivateMessages}
          placeholder="Select the user to send the message"
          onChange={handleMultiSelectChange}
          disabled={!isPrivateMessage}
          style={{
            width: '100%',
            minHeight: '32px',
            visibility: isPrivateMessage ? 'visible' : 'hidden'
          }}
        />
      </div>
    </Col>

    {/* Private Message Checkbox */}
    <Col xs="auto">
      <Form.Check
        type="checkbox"
        id="privateMessage"
        label="Private message"
        checked={isPrivateMessage}
        onChange={() => setIsPrivateMessage(!isPrivateMessage)}
        className="mb-0 text-nowrap"
      />
    </Col>

    {/* Attachments */}
    <Col xs="auto">
      <FileUpload setAttachmentHandler={setAttachmentHandler} />
    </Col>

  </Row>
</Card.Footer>



      </Card>
    </>
  );
};

Reply.propTypes = {
  setShowReplyForm: PropTypes.func,
  title: PropTypes.string,
  bodyClassName: PropTypes.string,
  onSubmitReply: PropTypes.func.isRequired,
  clearReplyFields: PropTypes.bool
};

export default Reply;